import React from 'react';
import { Card, Flex, Typography, Input, Select, Space, theme, Button } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';

import { type CDNAnalyticQueryParams } from './QueryParameters';

const OPTIONS = [
  { value: 'like', label: 'Contains' },
  { value: 'nlike', label: 'Does not contain' },
  { value: 'eq', label: 'Is equal to' },
  { value: 'neq', label: 'Is not equal to' },
  { value: 'sw', label: 'Starts with' },
  { value: 'ew', label: 'Ends with' },
];

type QueryConditionsDropdownProps = {
  value: string;
  onChange: (value: string) => void;
};

function QueryConditionsDropdown({ value, onChange }: QueryConditionsDropdownProps) {
  return <Select style={{ width: 160, flexShrink: 0 }} options={OPTIONS} value={value} onChange={onChange} virtual={false} />;
}

type ActionsProps = {
  onSearch: (q: string, qCondition: string) => void;
  onClear: () => void;
  onRefresh: () => void;
  onExportCSV: () => void;
  query?: CDNAnalyticQueryParams['q'];
  queryCondition?: CDNAnalyticQueryParams['q_condition'];
};

function Actions({ onSearch, onClear, onRefresh, onExportCSV, query, queryCondition }: ActionsProps) {
  const { token } = theme.useToken();
  const [selectedOption, setSelectedOption] = React.useState(queryCondition || OPTIONS[0].value);
  const [searchText, setSearchText] = React.useState(query || '');

  const debouncedSearch = React.useCallback(
    debounce((text, condition) => {
      if (text.length > 2 || !text) onSearch(text, condition);
    }, 300),
    [onSearch]
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);

    debouncedSearch(text, selectedOption);
  };

  const onConditionChange = (option: string) => {
    setSelectedOption(option);
    debouncedSearch(searchText, option);
  };

  React.useEffect(() => {
    setSelectedOption(queryCondition || OPTIONS[0].value);
    setSearchText(query || '');
  }, [query, queryCondition]);

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{ paddingTop: token.marginXS, paddingBottom: token.marginXS }}
      gap="small"
    >
      <Typography.Title level={5} style={{ margin: 0 }}>
        Crawler requests
      </Typography.Title>

      <Flex gap="small" wrap>
        <div>
          <Space.Compact>
            <QueryConditionsDropdown value={selectedOption} onChange={onConditionChange} />
            <Input
              styles={{ input: { minWidth: 170 } }}
              suffix={<SearchOutlined />}
              value={searchText}
              onChange={onInputChange}
              required
              minLength={3}
              placeholder="Search by URL (3+ chars)"
              allowClear
            />
          </Space.Compact>
        </div>
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          Refresh
        </Button>
        <Button type="text" onClick={onClear}>
          Clear filters
        </Button>
        <Button icon={<DownloadOutlined />} type="text" onClick={onExportCSV}>
          Export
        </Button>
      </Flex>
    </Flex>
  );
}

type CDNAnalyticsCardWithActionsProps = {
  updateFilters: (filters: Partial<CDNAnalyticQueryParams>) => void;
  clearFilters: () => void;
  refreshData: () => void;
  exportCSV: () => void;
  query?: CDNAnalyticQueryParams['q'];
  queryCondition?: CDNAnalyticQueryParams['q_condition'];
};

function CDNAnalyticsCardWithActions({
  children,
  updateFilters,
  clearFilters,
  refreshData,
  exportCSV,
  query,
  queryCondition,
}: React.PropsWithChildren<CDNAnalyticsCardWithActionsProps>) {
  const { token } = theme.useToken();

  const onSearch = (q: string, q_condition: string) => updateFilters({ q, q_condition });

  return (
    <Card
      styles={{ body: { padding: token.margin } }}
      title={
        <Actions
          onSearch={onSearch}
          onClear={clearFilters}
          onRefresh={refreshData}
          onExportCSV={exportCSV}
          query={query}
          queryCondition={queryCondition}
        />
      }
    >
      {children}
    </Card>
  );
}

export default CDNAnalyticsCardWithActions;
